import "./AddTemplateStepModal.css";

import { useContext } from "react";

import { TemplateStepConstants } from "../../../../../../../constants";
import { ProjectPhase } from "../../../../../../../constants/project-phase";
import { ServiceError } from "../../../../../../../service/Shared";
import { stringKeyToSentenceCase } from "../../../../../../../utils";
import {
  ActionButtons,
  Column,
  ErrorBox,
  KanaTooltip,
  Modal,
  RadioButtonGroup,
  Row,
  Select,
  TextInput,
} from "../../../../../../../widget";
import { WorkflowTemplateContext } from "../../WorkflowTemplateContext";
import { useAddTemplateStepModal } from "./useAddTemplateStepModal";

interface AddTemplateStepModalProps {
  show: boolean;
  closeModal: () => void;
  templateUuid?: string;
}

export const AddTemplateStepModal = ({ show, closeModal, templateUuid }: AddTemplateStepModalProps): JSX.Element => {
  const { workflowTemplatePhases } = useContext(WorkflowTemplateContext);
  const { step, setStep, handleAddTemplateStep, isHandleAddTemplateStepLoading, onCancel, errors } =
    useAddTemplateStepModal({
      closeModal,
      templateUuid,
    });
  const phaseSelectData = workflowTemplatePhases.map((item) => ({
    key: item.phase,
    value: stringKeyToSentenceCase(item.phase),
  }));
  const isOngoingMonitoringPhase = step.defaultPhase === ProjectPhase.ongoingMonitoring;

  return (
    <Modal show={show} title="Add step" onClose={onCancel}>
      {errors && errors.length > 0 && (
        <Row spacingV="ll">
          <Column offset={3} span={6}>
            <ErrorBox
              title="Please solve the below issues before saving"
              text={errors.map((error: ServiceError) => error.message)}
            />
          </Column>
        </Row>
      )}
      <form onSubmit={(e) => handleAddTemplateStep(e)}>
        <Row spacingV="ll">
          <Column offset={3} span={6}>
            <TextInput
              name="Step name"
              label="Step name*"
              placeholder="Add step name"
              value={step.defaultStepName}
              maxLength={100}
              onChange={(value) => setStep({ ...step, defaultStepName: value })}
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column offset={3} span={6}>
            <div className="AddTemplateStepModalPhaseSelectorWrapper">
              <Select
                label="Phase*"
                placeholder="Select phase"
                data={phaseSelectData}
                value={step.defaultPhase}
                onChange={(value) => setStep({ ...step, defaultPhase: value as string })}
              />
            </div>
          </Column>
        </Row>
        {isOngoingMonitoringPhase && (
          <Row spacingV="ll">
            <Column offset={3} span={6}>
              <RadioButtonGroup
                label="Is this step repeatable for future cycle of monitoring?"
                tooltip={
                  <KanaTooltip
                    tooltipHeader=""
                    tooltipText="Select this option to repeat this step and tasks in the next cycle of monitoring"
                  />
                }
                data={TemplateStepConstants.REPEATABLE_RADIO_BUTTON_DATA}
                onChange={(value) => setStep({ ...step, defaultIsRepeatable: value.target.value })}
              />
            </Column>
          </Row>
        )}
        <Row justify="center">
          <ActionButtons onCancel={onCancel} submitButtonLabel="Save" isLoading={isHandleAddTemplateStepLoading} />
        </Row>
      </form>
    </Modal>
  );
};
